/**
 * Port di ZionCipher.kt / CryptoManager.kt (ONYX/ZION E2E).
 * secp256r1 ECDH → AES-256-GCM, IV 12 byte + ciphertext, prefisso E2E:v1:
 */
import { arrayBufferToBase64, base64ToArrayBuffer, bytesToBase64 } from './base64.ts'

export const E2E_PREFIX = 'E2E:v1:'
export const PENDING_DECRYPT_PLACEHOLDER = '🔐 Messaggio in attesa di decifratura...'

const EC_GEN: EcKeyGenParams = { name: 'ECDH', namedCurve: 'P-256' }
const EC_IMPORT: EcKeyImportParams = { name: 'ECDH', namedCurve: 'P-256' }

export interface ZionKeyPair {
  publicKeyBase64: string
  privateKeyBase64: string
  privateKey: CryptoKey
}

export function normalizeNickname(nickname: string): string {
  return nickname.trim().toLowerCase()
}

export async function generateKeyPair(): Promise<ZionKeyPair> {
  // WebKit: AlgorithmIdentifier oggetto esatto (niente spread di tipi misti).
  const keyPair = await crypto.subtle.generateKey(EC_GEN, true, ['deriveBits'])

  const privatePkcs8 = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey)
  const publicSpki = await crypto.subtle.exportKey('spki', keyPair.publicKey)

  return {
    publicKeyBase64: arrayBufferToBase64(publicSpki),
    privateKeyBase64: arrayBufferToBase64(privatePkcs8),
    privateKey: keyPair.privateKey,
  }
}

export async function importPrivateKey(base64Pkcs8: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'pkcs8',
    base64ToArrayBuffer(base64Pkcs8),
    EC_IMPORT,
    true,
    ['deriveBits'],
  )
}

export async function importPublicKey(base64Spki: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'spki',
    base64ToArrayBuffer(base64Spki),
    EC_IMPORT,
    true,
    [],
  )
}

async function deriveAesKey(
  myPrivateKey: CryptoKey,
  theirPublicKey: CryptoKey,
): Promise<CryptoKey> {
  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: theirPublicKey },
    myPrivateKey,
    256,
  )

  return crypto.subtle.importKey(
    'raw',
    new Uint8Array(sharedBits),
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

/** Cifra UTF-8 → Base64(IV₁₂ ‖ ciphertext+tag). Senza prefisso E2E. */
export async function encrypt(
  myPrivateKey: CryptoKey,
  message: string,
  theirPublicKeyBase64: string,
): Promise<string> {
  const theirPublic = await importPublicKey(theirPublicKeyBase64)
  const aesKey = await deriveAesKey(myPrivateKey, theirPublic)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    new TextEncoder().encode(message),
  )

  const combined = new Uint8Array(iv.length + ciphertext.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(ciphertext), iv.length)
  return bytesToBase64(combined)
}

/** Decifra Base64(IV₁₂ ‖ ciphertext+tag) → UTF-8. */
export async function decrypt(
  myPrivateKey: CryptoKey,
  encryptedBase64: string,
  theirPublicKeyBase64: string,
): Promise<string> {
  const theirPublic = await importPublicKey(theirPublicKeyBase64)
  const aesKey = await deriveAesKey(myPrivateKey, theirPublic)
  const combined = new Uint8Array(base64ToArrayBuffer(encryptedBase64))
  const iv = combined.slice(0, 12)
  const data = combined.slice(12)

  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    data,
  )
  return new TextDecoder().decode(plain)
}

export async function encryptForContact(
  myPrivateKey: CryptoKey,
  plainText: string,
  contactPublicKeyBase64: string,
): Promise<string> {
  if (!contactPublicKeyBase64?.trim()) {
    throw new Error('IMPOSSIBILE CIFRARE: CHIAVE CONTATTO MANCANTE')
  }
  const body = await encrypt(myPrivateKey, plainText, contactPublicKeyBase64)
  return E2E_PREFIX + body
}

export async function decryptForContact(
  myPrivateKey: CryptoKey,
  payload: string,
  contactPublicKeyBase64: string,
): Promise<string> {
  const encryptedBody = stripE2EPrefix(payload)
  if (!encryptedBody) return payload
  return decrypt(myPrivateKey, encryptedBody, contactPublicKeyBase64)
}

export function stripE2EPrefix(payload: string): string | null {
  const t = payload.trim()
  if (t.startsWith(E2E_PREFIX)) return t.slice(E2E_PREFIX.length)
  if (/^E2E:v\d+:/i.test(t)) return t.replace(/^E2E:v\d+:/i, '')
  if (isLikelyCipherPayload(t)) return t
  return null
}

export function looksEncrypted(text: string): boolean {
  if (text === PENDING_DECRYPT_PLACEHOLDER) return true
  if (text.startsWith(E2E_PREFIX)) return true
  if (/^E2E:/i.test(text)) return true
  return isLikelyCipherPayload(text)
}

/** Base64 AES-GCM senza prefisso E2E (legacy o messaggio troncato). */
export function isLikelyCipherPayload(text: string): boolean {
  const t = text.trim()
  if (t.length < 24) return false
  if (t.startsWith('NYXIA_') || t.startsWith('{')) return false
  if (/^E2E:/i.test(t)) return t.length >= 32
  if (!/^[A-Za-z0-9+/=_-]+$/.test(t)) return false
  try {
    return base64ToArrayBuffer(t).byteLength >= 28
  } catch {
    return false
  }
}

export async function publicKeyHint(publicKeyBase64: string): Promise<string> {
  if (!publicKeyBase64.trim()) return ''
  const digest = await crypto.subtle.digest(
    { name: 'SHA-256' },
    new TextEncoder().encode(publicKeyBase64),
  )
  return Array.from(new Uint8Array(digest).slice(0, 8))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** Round-trip locale per verificare compatibilità cipher (dev / test). */
export async function selfTest(): Promise<{ ok: boolean; detail: string }> {
  const alice = await generateKeyPair()
  const bob = await generateKeyPair()
  const message = 'ZION E2E self-test 🔐'
  const sealed = await encryptForContact(alice.privateKey, message, bob.publicKeyBase64)
  const plain = await decryptForContact(bob.privateKey, sealed, alice.publicKeyBase64)
  const reverse = await encryptForContact(bob.privateKey, 'reply', alice.publicKeyBase64)
  const plain2 = await decryptForContact(alice.privateKey, reverse, bob.publicKeyBase64)
  const ok = plain === message && plain2 === 'reply' && sealed.startsWith(E2E_PREFIX)
  return { ok, detail: ok ? 'ECDH P-256 + AES-GCM OK' : `mismatch: ${plain} / ${plain2}` }
}
